import { useEffect } from 'react';

export default async function loadAssets(assets: Asset[]) {
  return Promise.all(
    assets.map(async ({ url, ...options }) => {
      await (url.endsWith('.css')
        ? injectCss(url, options)
        : injectJs(url, options));
    }),
  );
}

async function injectJs(url: string, options = {} as Record<string, string>) {
  if (options.id && document.getElementById(options.id)) {
    return undefined;
  }

  const script = document.createElement('script');
  script.src = url;
  Object.assign(script, options);
  window.document.head.append(script);
  return new Promise<void>((resolve, reject) => {
    script.addEventListener('error', (e) => {
      const text = e.error || e.message || 'Unknown error';
      reject(new Error(`Failed to load script ${url}: ${text}`));
    });
    script.addEventListener('load', () => resolve());
  });
}

async function injectCss(url: string, options = {} as Record<string, string>) {
  if (options.id && document.getElementById(options.id)) {
    return undefined;
  }

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = url;
  Object.assign(link, options);
  window.document.head.append(link);
  return new Promise<void>((resolve, reject) => {
    link.addEventListener('error', (e) => {
      const text = e.error || e.message || 'Unknown error';
      reject(new Error(`Failed to load style ${url}: ${text}`));
    });
    link.addEventListener('load', () => resolve());
  });
}

export function useCss(content: string, id = 'inline-css') {
  useEffect(() => {
    if (document.querySelector(`#${id}`)) {
      return undefined;
    }

    const style = document.createElement('style');
    style.id = id;
    style.innerHTML = content;
    window.document.head.append(style);
    return () => style.remove();
  }, [content, id]);
}

type Asset = {
  id: string;
  url: string;
} & Record<string, string>;
